import type { DeviceKey } from "../types"

/**
 * Step data for the eSIM install + connect flow, structured so adding a new
 * phone brand is a configuration change rather than a UI change. Steps live
 * here as pure data (no JSX, no Lucide imports) so they can be serialised,
 * fetched from a CMS later, or A/B-tested by swapping the array.
 *
 * i18n keys follow the convention:
 *   install.<deviceKey>.<method>.<stepId>.{title|body}
 *   install.<deviceKey>.connect.<stepId>.{title|body}
 *
 * Icons are referenced by name (StepIconName); the renderer maps name →
 * Lucide component. This keeps the data layer dependency-free.
 *
 * Source: Airalo public installation guide PDF (2026), cross-checked
 * against the official Apple / Samsung / Google / Xiaomi support pages.
 */

/**
 * Set of icons the step renderer knows how to draw. Keep this small — every
 * name here costs a Lucide bundle entry on the install page.
 */
export type StepIconName =
  | "settings" // gear / Settings app
  | "smartphone" // phone / device
  | "qr" // QR code
  | "keyboard" // manual input
  | "check" // confirmation
  | "hourglass" // waiting
  | "tag" // labeling eSIM
  | "database" // cellular data
  | "phone" // calls & texts
  | "globe" // roaming / network
  | "plus" // add (eSIM / plan)
  | "list" // menu / list
  | "signal" // signal / connected
  | "wifi" // Wi-Fi (for tips, not steps)

/** A single numbered step shown to the user. */
export type Step = {
  /** Stable id used as React key and in i18n key suffix. */
  id: string
  /** i18n key for the step title (short, imperative). */
  titleKey: string
  /** i18n key for the step body (1-2 sentence detail). */
  bodyKey: string
  /** Icon shown in the step's right-hand badge. */
  icon: StepIconName
  /**
   * Optional badge shown next to the title.
   *   - "first-time": only shown on the user's first eSIM (e.g. picking
   *     Primary line — iOS doesn't ask again after that).
   *   - "optional": user can usually skip this without breaking the flow.
   */
  badge?: "first-time" | "optional"
}

/** Static reference data the renderer shows alongside Connect steps. */
export type NetworkInfo = {
  /** i18n key for the APN value (or "auto" if the OS sets it). */
  apnKey: string
  /** Whether the user must enable data roaming for this carrier. */
  roaming: "on" | "off"
}

/** Complete flow for one DeviceKey. */
export type DeviceFlow = {
  install: {
    qr: Step[]
    manual: Step[]
    /** Only populated for iOS 17.4+ — a single-tap deeplink hand-off. */
    deeplink?: Step[]
  }
  connect: Step[]
  /** i18n key for the Settings menu path label (e.g. "Settings → Cellular"). */
  settingsPathKey: string
  /** Reference data shown alongside Connect steps. */
  networkInfo: NetworkInfo
}

// ─────────────────────────────────────────────────────────────────────────
// iOS
// ─────────────────────────────────────────────────────────────────────────

const iosQrSteps: Step[] = [
  {
    id: "s1",
    titleKey: "install.ios.qr.s1.title",
    bodyKey: "install.ios.qr.s1.body",
    icon: "settings",
  },
  {
    id: "s2",
    titleKey: "install.ios.qr.s2.title",
    bodyKey: "install.ios.qr.s2.body",
    icon: "qr",
  },
  {
    id: "s3",
    titleKey: "install.ios.qr.s3.title",
    bodyKey: "install.ios.qr.s3.body",
    icon: "hourglass",
  },
  {
    id: "s4",
    titleKey: "install.ios.qr.s4.title",
    bodyKey: "install.ios.qr.s4.body",
    icon: "tag",
  },
  {
    id: "s5",
    titleKey: "install.ios.qr.s5.title",
    bodyKey: "install.ios.qr.s5.body",
    icon: "phone",
    badge: "first-time",
  },
  {
    id: "s6",
    titleKey: "install.ios.qr.s6.title",
    bodyKey: "install.ios.qr.s6.body",
    icon: "database",
  },
]

const iosManualSteps: Step[] = [
  {
    id: "s1",
    titleKey: "install.ios.manual.s1.title",
    bodyKey: "install.ios.manual.s1.body",
    icon: "settings",
  },
  {
    id: "s2",
    titleKey: "install.ios.manual.s2.title",
    bodyKey: "install.ios.manual.s2.body",
    icon: "keyboard",
  },
  {
    id: "s3",
    titleKey: "install.ios.manual.s3.title",
    bodyKey: "install.ios.manual.s3.body",
    icon: "hourglass",
  },
  {
    id: "s4",
    titleKey: "install.ios.manual.s4.title",
    bodyKey: "install.ios.manual.s4.body",
    icon: "tag",
  },
  {
    id: "s5",
    titleKey: "install.ios.manual.s5.title",
    bodyKey: "install.ios.manual.s5.body",
    icon: "phone",
    badge: "first-time",
  },
  {
    id: "s6",
    titleKey: "install.ios.manual.s6.title",
    bodyKey: "install.ios.manual.s6.body",
    icon: "database",
  },
]

/** iOS 17.4+ deeplink: one tap on the device hands everything off to the OS. */
const iosDeeplinkSteps: Step[] = [
  {
    id: "s1",
    titleKey: "install.ios.deeplink.s1.title",
    bodyKey: "install.ios.deeplink.s1.body",
    icon: "smartphone",
  },
  {
    id: "s2",
    titleKey: "install.ios.deeplink.s2.title",
    bodyKey: "install.ios.deeplink.s2.body",
    icon: "check",
  },
]

const iosConnectSteps: Step[] = [
  {
    id: "s1",
    titleKey: "install.ios.connect.s1.title",
    bodyKey: "install.ios.connect.s1.body",
    icon: "signal",
  },
  {
    id: "s2",
    titleKey: "install.ios.connect.s2.title",
    bodyKey: "install.ios.connect.s2.body",
    icon: "database",
  },
  {
    id: "s3",
    titleKey: "install.ios.connect.s3.title",
    bodyKey: "install.ios.connect.s3.body",
    icon: "phone",
  },
  {
    id: "s4",
    titleKey: "install.ios.connect.s4.title",
    bodyKey: "install.ios.connect.s4.body",
    icon: "globe",
  },
]

// ─────────────────────────────────────────────────────────────────────────
// Android — Samsung
// ─────────────────────────────────────────────────────────────────────────

const samsungQrSteps: Step[] = [
  {
    id: "s1",
    titleKey: "install.android.samsung.qr.s1.title",
    bodyKey: "install.android.samsung.qr.s1.body",
    icon: "settings",
  },
  {
    id: "s2",
    titleKey: "install.android.samsung.qr.s2.title",
    bodyKey: "install.android.samsung.qr.s2.body",
    icon: "plus",
  },
  {
    id: "s3",
    titleKey: "install.android.samsung.qr.s3.title",
    bodyKey: "install.android.samsung.qr.s3.body",
    icon: "qr",
  },
  {
    id: "s4",
    titleKey: "install.android.samsung.qr.s4.title",
    bodyKey: "install.android.samsung.qr.s4.body",
    icon: "check",
  },
]

const samsungManualSteps: Step[] = [
  {
    id: "s1",
    titleKey: "install.android.samsung.manual.s1.title",
    bodyKey: "install.android.samsung.manual.s1.body",
    icon: "settings",
  },
  {
    id: "s2",
    titleKey: "install.android.samsung.manual.s2.title",
    bodyKey: "install.android.samsung.manual.s2.body",
    icon: "plus",
  },
  {
    id: "s3",
    titleKey: "install.android.samsung.manual.s3.title",
    bodyKey: "install.android.samsung.manual.s3.body",
    icon: "keyboard",
  },
  {
    id: "s4",
    titleKey: "install.android.samsung.manual.s4.title",
    bodyKey: "install.android.samsung.manual.s4.body",
    icon: "check",
  },
]

const samsungConnectSteps: Step[] = [
  {
    id: "s1",
    titleKey: "install.android.samsung.connect.s1.title",
    bodyKey: "install.android.samsung.connect.s1.body",
    icon: "signal",
  },
  {
    id: "s2",
    titleKey: "install.android.samsung.connect.s2.title",
    bodyKey: "install.android.samsung.connect.s2.body",
    icon: "database",
  },
  {
    id: "s3",
    titleKey: "install.android.samsung.connect.s3.title",
    bodyKey: "install.android.samsung.connect.s3.body",
    icon: "phone",
  },
  {
    id: "s4",
    titleKey: "install.android.samsung.connect.s4.title",
    bodyKey: "install.android.samsung.connect.s4.body",
    icon: "list",
  },
  {
    id: "s5",
    titleKey: "install.android.samsung.connect.s5.title",
    bodyKey: "install.android.samsung.connect.s5.body",
    icon: "globe",
  },
]

// ─────────────────────────────────────────────────────────────────────────
// Android — Pixel
// ─────────────────────────────────────────────────────────────────────────

const pixelQrSteps: Step[] = [
  {
    id: "s1",
    titleKey: "install.android.pixel.qr.s1.title",
    bodyKey: "install.android.pixel.qr.s1.body",
    icon: "settings",
  },
  {
    id: "s2",
    titleKey: "install.android.pixel.qr.s2.title",
    bodyKey: "install.android.pixel.qr.s2.body",
    icon: "plus",
  },
  {
    id: "s3",
    titleKey: "install.android.pixel.qr.s3.title",
    bodyKey: "install.android.pixel.qr.s3.body",
    icon: "qr",
  },
  {
    id: "s4",
    titleKey: "install.android.pixel.qr.s4.title",
    bodyKey: "install.android.pixel.qr.s4.body",
    icon: "check",
  },
]

const pixelManualSteps: Step[] = [
  {
    id: "s1",
    titleKey: "install.android.pixel.manual.s1.title",
    bodyKey: "install.android.pixel.manual.s1.body",
    icon: "settings",
  },
  {
    id: "s2",
    titleKey: "install.android.pixel.manual.s2.title",
    bodyKey: "install.android.pixel.manual.s2.body",
    icon: "plus",
  },
  {
    id: "s3",
    titleKey: "install.android.pixel.manual.s3.title",
    bodyKey: "install.android.pixel.manual.s3.body",
    icon: "keyboard",
  },
  {
    id: "s4",
    titleKey: "install.android.pixel.manual.s4.title",
    bodyKey: "install.android.pixel.manual.s4.body",
    icon: "check",
  },
]

const pixelConnectSteps: Step[] = [
  {
    id: "s1",
    titleKey: "install.android.pixel.connect.s1.title",
    bodyKey: "install.android.pixel.connect.s1.body",
    icon: "signal",
  },
  {
    id: "s2",
    titleKey: "install.android.pixel.connect.s2.title",
    bodyKey: "install.android.pixel.connect.s2.body",
    icon: "database",
  },
  {
    id: "s3",
    titleKey: "install.android.pixel.connect.s3.title",
    bodyKey: "install.android.pixel.connect.s3.body",
    icon: "phone",
  },
  {
    id: "s4",
    titleKey: "install.android.pixel.connect.s4.title",
    bodyKey: "install.android.pixel.connect.s4.body",
    icon: "list",
  },
  {
    id: "s5",
    titleKey: "install.android.pixel.connect.s5.title",
    bodyKey: "install.android.pixel.connect.s5.body",
    icon: "globe",
  },
]

// ─────────────────────────────────────────────────────────────────────────
// Android — Xiaomi
// ─────────────────────────────────────────────────────────────────────────

const xiaomiQrSteps: Step[] = [
  {
    id: "s1",
    titleKey: "install.android.xiaomi.qr.s1.title",
    bodyKey: "install.android.xiaomi.qr.s1.body",
    icon: "settings",
  },
  {
    id: "s2",
    titleKey: "install.android.xiaomi.qr.s2.title",
    bodyKey: "install.android.xiaomi.qr.s2.body",
    icon: "plus",
  },
  {
    id: "s3",
    titleKey: "install.android.xiaomi.qr.s3.title",
    bodyKey: "install.android.xiaomi.qr.s3.body",
    icon: "qr",
  },
  {
    id: "s4",
    titleKey: "install.android.xiaomi.qr.s4.title",
    bodyKey: "install.android.xiaomi.qr.s4.body",
    icon: "hourglass",
  },
  {
    id: "s5",
    titleKey: "install.android.xiaomi.qr.s5.title",
    bodyKey: "install.android.xiaomi.qr.s5.body",
    icon: "check",
  },
]

const xiaomiManualSteps: Step[] = [
  {
    id: "s1",
    titleKey: "install.android.xiaomi.manual.s1.title",
    bodyKey: "install.android.xiaomi.manual.s1.body",
    icon: "settings",
  },
  {
    id: "s2",
    titleKey: "install.android.xiaomi.manual.s2.title",
    bodyKey: "install.android.xiaomi.manual.s2.body",
    icon: "plus",
  },
  {
    id: "s3",
    titleKey: "install.android.xiaomi.manual.s3.title",
    bodyKey: "install.android.xiaomi.manual.s3.body",
    icon: "keyboard",
  },
  {
    id: "s4",
    titleKey: "install.android.xiaomi.manual.s4.title",
    bodyKey: "install.android.xiaomi.manual.s4.body",
    icon: "hourglass",
  },
  {
    id: "s5",
    titleKey: "install.android.xiaomi.manual.s5.title",
    bodyKey: "install.android.xiaomi.manual.s5.body",
    icon: "check",
  },
]

const xiaomiConnectSteps: Step[] = [
  {
    id: "s1",
    titleKey: "install.android.xiaomi.connect.s1.title",
    bodyKey: "install.android.xiaomi.connect.s1.body",
    icon: "signal",
  },
  {
    id: "s2",
    titleKey: "install.android.xiaomi.connect.s2.title",
    bodyKey: "install.android.xiaomi.connect.s2.body",
    icon: "database",
  },
  {
    id: "s3",
    titleKey: "install.android.xiaomi.connect.s3.title",
    bodyKey: "install.android.xiaomi.connect.s3.body",
    icon: "list",
  },
  {
    id: "s4",
    titleKey: "install.android.xiaomi.connect.s4.title",
    bodyKey: "install.android.xiaomi.connect.s4.body",
    icon: "globe",
  },
]

// ─────────────────────────────────────────────────────────────────────────
// Android — Generic (OPPO / Vivo / Huawei / OnePlus / Motorola / …)
//
// Catch-all that papers over manufacturer differences with terms users can
// pattern-match against. Whenever a brand starts generating enough support
// load to deserve its own pinned flow, split it out into a dedicated block
// above and add the DeviceKey to the union in types.ts.
// ─────────────────────────────────────────────────────────────────────────

const genericQrSteps: Step[] = [
  {
    id: "s1",
    titleKey: "install.android.generic.qr.s1.title",
    bodyKey: "install.android.generic.qr.s1.body",
    icon: "settings",
  },
  {
    id: "s2",
    titleKey: "install.android.generic.qr.s2.title",
    bodyKey: "install.android.generic.qr.s2.body",
    icon: "plus",
  },
  {
    id: "s3",
    titleKey: "install.android.generic.qr.s3.title",
    bodyKey: "install.android.generic.qr.s3.body",
    icon: "qr",
  },
  {
    id: "s4",
    titleKey: "install.android.generic.qr.s4.title",
    bodyKey: "install.android.generic.qr.s4.body",
    icon: "check",
  },
]

const genericManualSteps: Step[] = [
  {
    id: "s1",
    titleKey: "install.android.generic.manual.s1.title",
    bodyKey: "install.android.generic.manual.s1.body",
    icon: "settings",
  },
  {
    id: "s2",
    titleKey: "install.android.generic.manual.s2.title",
    bodyKey: "install.android.generic.manual.s2.body",
    icon: "plus",
  },
  {
    id: "s3",
    titleKey: "install.android.generic.manual.s3.title",
    bodyKey: "install.android.generic.manual.s3.body",
    icon: "keyboard",
  },
  {
    id: "s4",
    titleKey: "install.android.generic.manual.s4.title",
    bodyKey: "install.android.generic.manual.s4.body",
    icon: "check",
  },
]

const genericConnectSteps: Step[] = [
  {
    id: "s1",
    titleKey: "install.android.generic.connect.s1.title",
    bodyKey: "install.android.generic.connect.s1.body",
    icon: "signal",
  },
  {
    id: "s2",
    titleKey: "install.android.generic.connect.s2.title",
    bodyKey: "install.android.generic.connect.s2.body",
    icon: "database",
  },
  {
    id: "s3",
    titleKey: "install.android.generic.connect.s3.title",
    bodyKey: "install.android.generic.connect.s3.body",
    icon: "list",
  },
  {
    id: "s4",
    titleKey: "install.android.generic.connect.s4.title",
    bodyKey: "install.android.generic.connect.s4.body",
    icon: "globe",
  },
]

// ─────────────────────────────────────────────────────────────────────────
// Top-level flow registry
// ─────────────────────────────────────────────────────────────────────────

export const flows: Record<DeviceKey, DeviceFlow> = {
  ios: {
    install: {
      qr: iosQrSteps,
      manual: iosManualSteps,
      deeplink: iosDeeplinkSteps,
    },
    connect: iosConnectSteps,
    settingsPathKey: "install.ios.settingsPath",
    networkInfo: {
      apnKey: "install.ios.network.apnAuto",
      roaming: "on",
    },
  },
  "android-samsung": {
    install: { qr: samsungQrSteps, manual: samsungManualSteps },
    connect: samsungConnectSteps,
    settingsPathKey: "install.android.samsung.settingsPath",
    networkInfo: {
      apnKey: "install.android.common.apnWbdata",
      roaming: "on",
    },
  },
  "android-pixel": {
    install: { qr: pixelQrSteps, manual: pixelManualSteps },
    connect: pixelConnectSteps,
    settingsPathKey: "install.android.pixel.settingsPath",
    networkInfo: {
      apnKey: "install.android.common.apnWbdata",
      roaming: "on",
    },
  },
  "android-xiaomi": {
    install: { qr: xiaomiQrSteps, manual: xiaomiManualSteps },
    connect: xiaomiConnectSteps,
    settingsPathKey: "install.android.xiaomi.settingsPath",
    networkInfo: {
      apnKey: "install.android.common.apnWbdata",
      roaming: "on",
    },
  },
  "android-generic": {
    install: { qr: genericQrSteps, manual: genericManualSteps },
    connect: genericConnectSteps,
    settingsPathKey: "install.android.generic.settingsPath",
    networkInfo: {
      apnKey: "install.android.common.apnWbdata",
      roaming: "on",
    },
  },
}

/** Convenience accessor — used by the renderer once it knows the DeviceKey. */
export function getFlow(deviceKey: DeviceKey): DeviceFlow {
  return flows[deviceKey]
}
